import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowRight,
  Gauge,
  HeartHandshake,
  ShieldCheck,
  FileText,
  Scale,
  Users,
  Library,
} from "lucide-react";
import CircleGlow from "../components/CircleGlow";

/**
 * www.riskaia.com — Strategic editorial landing page.
 *
 * Five-layer structure:
 *   1) Hero            — definitive brand poster (logo + tagline + footer
 *                        all baked into a single image) + two CTAs + trust
 *                        seal
 *   2) Urgency         — "Van papieren tijger naar realtime grip"
 *   3) Mission         — centered glowing manifesto card
 *   4) Six pillars     — Toerenteller · Lokale waarden · Bewijslast ·
 *                        EU AI Act · Fundamentele rechten · Algoritmeregister
 *   5) Final CTA       — single oversized button into the live demo
 *
 * Pure Lounge Doctrine: #1C3A5A canvas, Fraunces for headers, Outfit body.
 */

const DEMO_ROUTE = "/platform";
const DOSSIER_ROUTE = "/kaders";

// Definitive landing-hero brand asset. Includes the mark, the "CONTINU
// GECERTIFICEERD AI" tagline and the "voor de mienskip" footer baked into
// the image, so the surrounding HTML stays minimal.
const HERO_BRAND_URL =
  "https://customer-assets.emergentagent.com/wingman/368cca20-abd0-4588-99d9-9e42b06e9661/attachments/2bbd7681a8a04908b46aa22122b6939c_Gemini_Generated_Image_32yxxe32yxxe32yx.png";

const HERO_MASK =
  "radial-gradient(ellipse 85% 85% at center, black 60%, transparent 100%)";

const Home = () => (
  <div data-testid="page-home">
    {/* ============================================================
        LAYER 1 — HERO
       ============================================================ */}
    <section
      className="relative overflow-hidden"
      data-testid="home-hero"
    >
      <CircleGlow intensity={1.1} />

      <div className="rk-container relative pt-10 md:pt-16 pb-20 md:pb-24">
        {/* Definitive brand poster — mark + "CONTINU GECERTIFICEERD AI"
            tagline + "voor de mienskip" footer all baked into the image. */}
        <div
          className="flex justify-center"
          data-testid="home-hero-emblem"
          style={{
            background: "transparent",
            border: 0,
            outline: 0,
            padding: 0,
            margin: 0,
            lineHeight: 0,
          }}
        >
          <img
            src={HERO_BRAND_URL}
            alt="RiskaiΛ — Continu gecertificeerd AI voor de Mienskip"
            draggable={false}
            data-testid="home-hero-emblem-img"
            style={{
              width: "520px",
              maxWidth: "92vw",
              height: "auto",
              display: "block",
              mixBlendMode: "lighten",
              maskImage: HERO_MASK,
              WebkitMaskImage: HERO_MASK,
              maskRepeat: "no-repeat",
              WebkitMaskRepeat: "no-repeat",
              maskSize: "100% 100%",
              WebkitMaskSize: "100% 100%",
              background: "transparent",
              border: "none",
              pointerEvents: "none",
            }}
          />
        </div>

        {/* Subtitle — distinct explanatory body (no overlap with image text) */}
        <p
          className="mt-8 max-w-3xl mx-auto text-center text-lg md:text-xl text-[#D4D8DF] leading-relaxed"
          data-testid="home-subtitle"
        >
          RiskaiΛ transformeert complexe Europese AI-wetgeving en
          VNG-richtlijnen naar realtime bestuurlijke controle. Voor overheden
          die sturen op compliance{" "}
          <span className="text-white italic">én</span> menselijke waardigheid.
        </p>

        {/* Two CTAs */}
        <div
          className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-5"
          data-testid="home-cta-row"
        >
          <Link
            to={DEMO_ROUTE}
            className="rk-btn-cyan"
            data-testid="home-cta-demo"
          >
            Start de live demo
            <ArrowUpRight strokeWidth={1.25} className="w-4 h-4" />
          </Link>
          <Link
            to={DOSSIER_ROUTE}
            className="rk-btn-ghost"
            data-testid="home-cta-dossier"
          >
            <FileText strokeWidth={1.25} className="w-4 h-4" />
            Lees ons strategisch dossier
          </Link>
        </div>

        {/* Trust signal — Leeuwarden municipality crest */}
        <div
          className="mt-16 flex flex-col items-center gap-3"
          data-testid="home-trust-seal"
        >
          <div
            className="text-[10px] tracking-[0.28em] uppercase text-center"
            style={{ color: "#9AA4B2" }}
          >
            Gevalideerd voor de Mienskip{" "}
            <span style={{ color: "#5470A3" }}>·</span> In samenwerking met
            Gemeente Leeuwarden
          </div>
          <img
            src="https://customer-assets.emergentagent.com/wingman/368cca20-abd0-4588-99d9-9e42b06e9661/attachments/d32f935dacb74e34a929d8d2c7f15683_image.png"
            alt="Gemeente Leeuwarden"
            draggable={false}
            data-testid="home-trust-seal-img"
            style={{
              height: "48px",
              width: "48px",
              objectFit: "cover",
              opacity: 0.8,
              borderRadius: "6px",
              border: "1px solid rgba(192,197,206,0.20)",
              mixBlendMode: "lighten",
            }}
          />
        </div>
      </div>
      <div className="hairline" />
    </section>

    {/* ============================================================
        LAYER 2 — URGENCY
       ============================================================ */}
    <section
      className="relative overflow-hidden"
      data-testid="home-urgency"
    >
      <div className="rk-container relative py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
          <div className="md:col-span-5">
            <div className="overline mb-5" data-testid="home-urgency-eyebrow">
              De Urgentie
            </div>
            <h2
              className="text-white tracking-tight leading-[1.05] text-4xl md:text-5xl"
              style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
              data-testid="home-urgency-title"
            >
              Van papieren tijger
              <br />
              <em
                className="italic text-[#D4D8DF]"
                style={{ fontFamily: "Fraunces, serif" }}
              >
                naar realtime grip.
              </em>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 space-y-6">
            <p className="text-lg text-[#D4D8DF] leading-relaxed">
              De AI-Verordening, de AVG en het VNG-kader vragen méér dan een
              jaarlijks bijgewerkte Excel. Algoritmes leven: ze hertrainen,
              krijgen nieuwe databronnen, raken onderhevig aan jurisprudentie.
              Een statisch register kan deze beweging per definitie niet
              volgen.
            </p>
            <p className="text-lg text-[#D4D8DF] leading-relaxed">
              Toezichthouders, journalisten en burgers verwachten op elk
              moment een eerlijk antwoord op één vraag:{" "}
              <span className="text-white italic">
                wat doet dit algoritme nú, en wie staat ervoor in?
              </span>
            </p>
            <div
              className="border-l-2 pl-6 py-3"
              style={{ borderColor: "#5BC0EB" }}
              data-testid="home-urgency-pullquote"
            >
              <p
                className="text-2xl md:text-3xl text-white tracking-tight leading-snug"
                style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
              >
                Compliance is geen{" "}
                <em
                  className="italic"
                  style={{ color: "#7FD7E4" }}
                >
                  vinkje achteraf.
                </em>
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="hairline" />
    </section>

    {/* ============================================================
        LAYER 3 — MISSION
       ============================================================ */}
    <section
      className="relative overflow-hidden"
      data-testid="home-mission"
    >
      <CircleGlow intensity={0.65} />
      <div className="rk-container relative py-24 md:py-32">
        <div className="max-w-4xl mx-auto text-center">
          <div className="overline mb-6">Het hogere doel</div>
        </div>
        <div
          className="max-w-4xl mx-auto relative"
          data-testid="home-mission-card"
          style={{
            background:
              "linear-gradient(180deg, rgba(91,192,235,0.06) 0%, rgba(91,192,235,0) 100%)",
            border: "1px solid rgba(192,197,206,0.30)",
            padding: "3rem 2.5rem",
            boxShadow: "0 30px 90px -30px rgba(91,192,235,0.18)",
          }}
        >
          {/* Top hairline accent */}
          <div
            className="absolute left-1/2 -translate-x-1/2 -top-px h-px w-24"
            style={{ background: "#5BC0EB" }}
          />
          <p
            className="text-2xl md:text-3xl text-white text-center tracking-tight leading-[1.35]"
            style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
            data-testid="home-mission-text"
          >
            Technologie mag nooit losstaan van de samenleving die zij dient.
            De missie van{" "}
            <em
              className="italic rk-wordmark"
              style={{ fontFamily: "Fraunces, serif", color: "#7FD7E4" }}
            >
              RiskaiΛ
            </em>{" "}
            is om overheden en organisaties te voorzien van een onwrikbaar
            digitaal mandaat.
          </p>
          <p
            className="mt-7 text-lg text-[#D4D8DF] text-center leading-relaxed max-w-3xl mx-auto"
          >
            Wij democratiseren compliance door complexe algoritmen
            transparant, toetsbaar en ethisch verantwoord te maken.
            Transparantie is in onze visie geen gunst aan de burger, maar de
            enige weg naar een houdbare en rechtvaardige digitale Mienskip.
          </p>
        </div>
      </div>
      <div className="hairline" />
    </section>

    {/* ============================================================
        LAYER 4 — SIX PILLARS
       ============================================================ */}
    <section
      className="relative overflow-hidden"
      data-testid="home-pillars"
    >
      <div className="rk-container relative py-24 md:py-32">
        <div className="max-w-3xl mb-16">
          <div className="overline mb-5">Zes Pijlers</div>
          <h2
            className="text-white tracking-tight leading-[1.05] text-4xl md:text-5xl"
            style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
            data-testid="home-pillars-title"
          >
            Hoe <span className="rk-wordmark">RiskaiΛ</span>
            <br />
            <em
              className="italic text-[#D4D8DF]"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              het verschil maakt.
            </em>
          </h2>
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#C0C5CE]/15"
          data-testid="home-pillars-grid"
        >
          <Pillar
            number="01"
            icon={Gauge}
            title="Realtime Governance"
            subtitle="De Toerenteller"
            body="Vergeet statische kwartaalrapporten. RiskaiΛ vertaalt uw lopende projecten naar een dynamische Readiness Score. Zodra een algoritme afwijkt van de gestelde kaders, ziet het bestuur dit direct realtime op het dashboard."
            testid="pillar-1"
          />
          <Pillar
            number="02"
            icon={HeartHandshake}
            title="Borging van Lokale Waarden"
            subtitle="Ethische Toets & Menselijke Maat"
            body="Beleidsplannen worden praktijk. Het platform dwingt bij elk algoritme een harde toetsing af op de 8 ethische waarden van de gemeente, waaronder Menselijke Waardigheid, Rechtvaardigheid, Autonomie en de Mienskip."
            testid="pillar-2"
          />
          <Pillar
            number="03"
            icon={ShieldCheck}
            title="Onweerlegbare Bewijslast"
            subtitle="Cryptografisch Dossier"
            body="Elke stap en beslissing binnen het ethische traject wordt onwrikbaar vastgelegd via ingebouwde opensource DocuSeal/Documenso-validatie. Dit vormt één sluitende audit trail voor de accountant, de Rekenkamer of de Autoriteit Persoonsgegevens."
            testid="pillar-3"
          />
          <Pillar
            number="04"
            icon={Scale}
            title="EU AI Act Risicoklassificatie"
            subtitle="Automatische Risico Klassen"
            body="Geen handmatig uitzoekwerk voor juristen. De ingebouwde wizard classificeert algoritmen volledig automatisch conform de Europese AI-Verordening en activeert direct de bijbehorende wettelijke documentatie- en transparantieverplichtingen."
            testid="pillar-4"
          />
          <Pillar
            number="05"
            icon={Users}
            title="Fundamentele Rechten & IAMA"
            subtitle="Digitale Mensenrechtentoets"
            body="Het verplichte Impact Assessment Mensenrechten bij de inzet van Algoritmen (IAMA) is volledig gedigitaliseerd. Risico's op bias, stereotypering of profilering worden direct in de ontwerpfase gesignaleerd en ondervangen."
            testid="pillar-5"
          />
          <Pillar
            number="06"
            icon={Library}
            title="Kwaliteitsnormen & Algoritmeregister"
            subtitle="BIO · Algoritmewijzer · Register"
            body="RiskaiΛ loodst projecten automatisch door de 3 formele governance-routes van de gemeente (van het interne 'Goede Gesprek' tot escalatie naar de Data-ethykkommisje Fryslân). Na accreditatie voedt het systeem direct het verplichte landelijke Algoritmeregister én uw lokale moresprudentieregister."
            testid="pillar-6"
          />
        </div>
      </div>
      <div className="hairline" />
    </section>

    {/* ============================================================
        LAYER 5 — FINAL CTA
       ============================================================ */}
    <section
      className="relative overflow-hidden"
      data-testid="home-final-cta"
    >
      <CircleGlow intensity={0.9} />
      <div className="rk-container relative py-28 md:py-36 text-center">
        <div className="overline mb-6" data-testid="home-final-eyebrow">
          De ervaring zelf
        </div>
        <h2
          className="text-white max-w-4xl mx-auto tracking-tight leading-[1.05] text-4xl md:text-6xl"
          style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
          data-testid="home-final-title"
        >
          Ervaar de <span className="rk-wordmark">RiskaiΛ</span>
          <br />
          <em
            className="italic text-[#D4D8DF]"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            Cockpit zelf.
          </em>
        </h2>
        <p
          className="mt-7 max-w-2xl mx-auto text-lg text-[#D4D8DF] leading-relaxed"
        >
          Geen sales-deck, geen video. Een werkende cockpit met vier
          gepubliceerde AI-systemen van Gemeente Leeuwarden — open en
          inspecteerbaar.
        </p>
        <div className="mt-12 flex justify-center">
          <Link
            to={DEMO_ROUTE}
            className="inline-flex items-center gap-3 px-8 py-4 text-[13px] tracking-[0.22em] uppercase bg-[#5BC0EB] text-[#0F1E33] hover:bg-[#7FD7E4] transition-colors duration-300"
            data-testid="home-final-cta-btn"
            style={{
              boxShadow: "0 24px 60px -20px rgba(91,192,235,0.45)",
              fontWeight: 400
            }}
          >
            Open de <span className="rk-wordmark">RiskaiΛ</span> demo
            <ArrowRight strokeWidth={1.5} className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  </div>
);

// ---------- Pillar card ----------
const Pillar = ({ number, icon: Icon, title, subtitle, body, testid }) => (
  <article
    data-testid={testid}
    className="bg-[#162E4A] hover:bg-[#18335A] transition-colors duration-500 p-10 md:p-12 flex flex-col"
  >
    <div className="flex items-start justify-between gap-6 mb-8">
      <div className="overline">{number}</div>
      <div
        className="shrink-0 w-14 h-14 border border-[#C0C5CE]/40 flex items-center justify-center"
        aria-hidden
      >
        <Icon strokeWidth={1} className="w-7 h-7 text-[#C0C5CE]" />
      </div>
    </div>
    <div
      className="text-[11px] tracking-[0.22em] uppercase mb-3 min-h-[2.25rem] flex items-end"
      style={{ color: "#7FD7E4" }}
    >
      {subtitle}
    </div>
    <h3
      className="text-3xl text-white tracking-tight leading-tight mb-6 min-h-[4.75rem]"
      style={{ fontFamily: "Fraunces, serif", fontWeight: 400 }}
    >
      {title}
    </h3>
    <p className="text-[#D4D8DF] leading-relaxed flex-1">{body}</p>
  </article>
);

export default Home;
